import { newId } from "../domain/seed.ts";
import { compareMonths, monthOf } from "../domain/months.ts";
import { toDayOrdinal } from "../domain/days.ts";
import {
  chargeDateNeedsDay,
  isDayGranularDate,
  startDateNeedsDay,
} from "../domain/granularity.ts";
import { roundMoney } from "../domain/money.ts";
import { currencyUsage, normalizeCurrencyCode } from "../domain/currencies.ts";
import type {
  Anchoring,
  Currency,
  CurrencyDef,
  Dataset,
  BudgetFolder,
  FolderId,
  FxRate,
  IsoDate,
  Money,
  Month,
  MonthId,
  Post,
  PostId,
  Purchase,
  PurchaseId,
  Recurrence,
  RecurringCost,
  RecurringCostId,
  Rule,
  RuleVersion,
  Schedule,
  Split,
} from "../domain/types.ts";

/**
 * Every function here mutates a draft Dataset in place. They are called from
 * store.mutate(), which clones before applying and writes through afterwards.
 *
 * Every MONEY amount that flows through here is rounded to the dataset's
 * decimal places (via roundMoney) before it lands in the draft, per AGENTS.md's
 * "round before persisting" rule. FxRate.baseUnitsPerOne is deliberately
 * exempted in setFxRate below — an exchange rate is not money, and rounding
 * it to cents would be wrong in kind (see the comment there).
 */

function requirePost(draft: Dataset, postId: PostId): Post {
  const post = draft.posts.find((p) => p.id === postId);
  if (!post) throw new Error(`Unknown post: ${postId}`);
  return post;
}

export type BudgetItemKey = { kind: "folder" | "post" | "recurring" | "purchase"; id: string };
type BudgetItem = BudgetFolder | Post | RecurringCost | Purchase;
type BudgetSibling = BudgetItemKey & { order: number };
const orderOf = (item: BudgetItem): number => "placement" in item && item.placement ? item.placement.order : (item as BudgetFolder | Post | RecurringCost).order;
const setOrder = (item: BudgetItem, order: number): void => { if ("placement" in item && item.placement) item.placement.order = order; else (item as BudgetFolder | Post | RecurringCost).order = order; };

function siblingRefs(draft: Dataset, parentId: FolderId | null): Array<{ key: BudgetItemKey; item: BudgetItem }> {
  return [
    ...draft.folders.filter(item => item.parentId === parentId).map(item => ({ key: { kind: "folder" as const, id: item.id }, item })),
    ...draft.posts.filter(item => item.folderId === parentId).map(item => ({ key: { kind: "post" as const, id: item.id }, item })),
    ...draft.recurring.filter(item => !item.parentPostId && item.folderId === parentId).map(item => ({ key: { kind: "recurring" as const, id: item.id }, item })),
    ...draft.purchases.filter(item => item.placement?.folderId === parentId).map(item => ({ key: { kind: "purchase" as const, id: item.id }, item })),
  ].sort((a, b) => orderOf(a.item) - orderOf(b.item));
}

/** A clock-free, FX-free view of the shared order for Settings and placement. */
export function budgetSiblings(draft: Dataset, parentId: FolderId | null): BudgetSibling[] {
  return siblingRefs(draft, parentId).map(({ key, item }) => ({ ...key, order: orderOf(item) }));
}

function nextSiblingOrder(draft: Dataset, parentId: FolderId | null): number {
  const siblings = siblingRefs(draft, parentId);
  if (!siblings.length) return 0;
  const highest = orderOf(siblings[siblings.length - 1]!.item);
  const next = highest + 1;
  if (Number.isFinite(next) && next > highest) return next;
  siblings.forEach(({ item }, order) => setOrder(item, order));
  return siblings.length;
}

function requireFolder(draft: Dataset, id: FolderId): BudgetFolder {
  const folder = draft.folders.find(item => item.id === id);
  if (!folder) throw new Error(`Unknown folder: ${id}`);
  return folder;
}

function requireFolderName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Folder name cannot be blank");
  return trimmed;
}

function requireBudgetItem(draft: Dataset, key: BudgetItemKey): BudgetItem {
  if (key.kind === "folder") return requireFolder(draft, key.id);
  if (key.kind === "post") return requirePost(draft, key.id);
  if (key.kind === "recurring") return requireRecurringCost(draft, key.id);
  if (key.kind === "purchase") return requirePurchase(draft, key.id);
  throw new Error(`Unknown budget item kind: ${String(key.kind)}`);
}

function itemParent(key: BudgetItemKey, item: BudgetItem): FolderId | null {
  return key.kind === "folder" ? (item as BudgetFolder).parentId : key.kind === "purchase" ? (item as Purchase).placement!.folderId : (item as Post | RecurringCost).folderId;
}

export function addBudgetFolder(draft: Dataset, name: string, parentId: FolderId | null): BudgetFolder {
  const resolvedName = requireFolderName(name);
  if (parentId !== null) requireFolder(draft, parentId);
  const folder: BudgetFolder = { id: newId(), name: resolvedName, parentId, order: nextSiblingOrder(draft, parentId) };
  draft.folders.push(folder);
  return folder;
}

export function renameBudgetFolder(draft: Dataset, id: FolderId, name: string): void {
  const folder = requireFolder(draft, id);
  const resolvedName = requireFolderName(name);
  folder.name = resolvedName;
}

/** Removes only the container; its direct children take its place in the parent. */
export function removeBudgetFolder(draft: Dataset, id: FolderId): void {
  const folder = requireFolder(draft, id);
  const parent = folder.parentId;
  const siblings = siblingRefs(draft, parent);
  const position = siblings.findIndex(entry => entry.key.kind === "folder" && entry.key.id === id);
  const children = siblingRefs(draft, id);
  for (const { key, item } of children) {
    if (key.kind === "folder") (item as BudgetFolder).parentId = parent;
    else if (key.kind === "purchase") (item as Purchase).placement!.folderId = parent;
    else (item as Post | RecurringCost).folderId = parent;
  }
  draft.folders.splice(draft.folders.indexOf(folder), 1);
  siblings.splice(position, 1, ...children);
  siblings.forEach(({ item }, order) => setOrder(item, order));
}

export function placeBudgetItem(draft: Dataset, key: BudgetItemKey, destination: FolderId | null, index: number): void {
  const item = requireBudgetItem(draft, key);
  if (destination !== null) requireFolder(draft, destination);
  if (key.kind === "folder") {
    let cursor = destination;
    while (cursor !== null) {
      if (cursor === key.id) throw new Error("A folder cannot be moved into itself or a descendant");
      cursor = requireFolder(draft, cursor).parentId;
    }
  }
  const source = itemParent(key, item);
  const sourceItems = siblingRefs(draft, source).filter(entry => !(entry.key.kind === key.kind && entry.key.id === key.id));
  const destinationItems = source === destination ? sourceItems : siblingRefs(draft, destination);
  if (!Number.isInteger(index) || index < 0 || index > destinationItems.length) {
    throw new Error(`Invalid sibling index: ${index}`);
  }
  if (key.kind === "folder") (item as BudgetFolder).parentId = destination;
  else if (key.kind === "purchase") (item as Purchase).placement!.folderId = destination;
  else {
    (item as Post | RecurringCost).folderId = destination;
    if (key.kind === "recurring") {
      const cost = item as RecurringCost;
      if (cost.parentPostId) draft.recurring.filter(other => other.parentPostId === cost.parentPostId && other.id !== cost.id).sort((a, b) => a.order - b.order).forEach((other, position) => { other.order = position; });
      delete cost.parentPostId;
    }
  }
  destinationItems.splice(index, 0, { key, item });
  if (source !== destination) sourceItems.forEach((entry, order) => setOrder(entry.item, order));
  destinationItems.forEach((entry, order) => setOrder(entry.item, order));
}

export function placePurchase(draft: Dataset, purchaseId: PurchaseId, folderId: FolderId | null | undefined): void {
  const purchase = requirePurchase(draft, purchaseId);
  if (folderId !== undefined && folderId !== null) requireFolder(draft, folderId);
  if (purchase.placement) {
    siblingRefs(draft, purchase.placement.folderId).filter(entry => !(entry.key.kind === "purchase" && entry.key.id === purchaseId)).forEach(({ item }, order) => setOrder(item, order));
  }
  delete purchase.placement;
  if (folderId === undefined) delete purchase.placement;
  else purchase.placement = { folderId, order: nextSiblingOrder(draft, folderId) };
}

export function placeRecurringUnderPost(draft: Dataset, recurringId: RecurringCostId, postId: PostId): void {
  requirePost(draft, postId);
  const cost = requireRecurringCost(draft, recurringId);
  const formerPostId = cost.parentPostId;
  if (!cost.parentPostId) siblingRefs(draft, cost.folderId).filter(entry => !(entry.key.kind === "recurring" && entry.key.id === recurringId)).forEach(({ item }, order) => setOrder(item, order));
  if (formerPostId) draft.recurring.filter(other => other.parentPostId === formerPostId && other.id !== recurringId).sort((a, b) => a.order - b.order).forEach((other, index) => { other.order = index; });
  cost.folderId = null;
  cost.parentPostId = postId;
  cost.order = draft.recurring.filter(other => other.parentPostId === postId && other.id !== recurringId).length;
}

function requirePurchase(draft: Dataset, purchaseId: PurchaseId): Purchase {
  const purchase = draft.purchases.find((p) => p.id === purchaseId);
  if (!purchase) throw new Error(`Unknown purchase: ${purchaseId}`);
  return purchase;
}

/** Rounds a non-money percentage (e.g. Rule.percent, Split.value) to 2 decimals. */
function round2(value: number): number {
  return Number(value.toFixed(2));
}

function roundMoneyValue(draft: Dataset, money: Money): Money {
  return {
    amount: roundMoney(money.amount, draft.settings.digits),
    currency: money.currency,
  };
}

function roundRule(draft: Dataset, rule: Rule): Rule {
  if (rule.kind === "fixed") {
    return { kind: "fixed", amount: roundMoneyValue(draft, rule.amount) };
  }
  return { kind: "percentOfIncome", percent: round2(rule.percent) };
}

/**
 * A split's `value` is a percentage in "percent" mode and MONEY in the
 * purchase's currency in "fixed" mode, so the two round differently: a
 * percentage always to 2 decimals, money to the dataset's decimal places.
 * Rounding both to a hardcoded 2 is correct only while that setting is 2.
 */
function roundSplits(
  draft: Dataset,
  splits: Split[],
  splitMode: Purchase["splitMode"],
  _currency: Currency,
): Split[] {
  const digits = draft.settings.digits;
  return splits.map((split) => ({
    ...split,
    value: splitMode === "fixed" ? roundMoney(split.value, digits) : round2(split.value),
  }));
}

function requireRecurringSplitDestinations(draft: Dataset, splits: Split[]): void {
  for (const split of splits) {
    const post = requirePost(draft, split.postId);
    if (post.archived) throw new Error(`Post ${post.name} is archived`);
  }
}

function roundSchedule(draft: Dataset, schedule: Schedule | null): Schedule | null {
  if (!schedule) return null;
  return {
    ...schedule,
    slices: schedule.slices.map((slice) => ({
      month: slice.month,
      amount: roundMoneyValue(draft, slice.amount),
    })),
  };
}

export function ensureMonth(draft: Dataset, monthId: MonthId): Month {
  const existing = draft.months.find((m) => m.id === monthId);
  if (existing) return existing;
  const created: Month = {
    id: monthId,
    income: { amount: 0, currency: draft.settings.baseCurrency },
    ruleOverrides: {},
  };
  draft.months.push(created);
  draft.months.sort((a, b) => a.id.localeCompare(b.id));
  return created;
}

export function setIncome(draft: Dataset, monthId: MonthId, income: Money): void {
  ensureMonth(draft, monthId).income = roundMoneyValue(draft, income);
}

export function setRuleOverride(
  draft: Dataset,
  monthId: MonthId,
  postId: PostId,
  rule: Rule | null,
): void {
  const month = ensureMonth(draft, monthId);
  if (rule === null) {
    delete month.ruleOverrides[postId];
    return;
  }
  month.ruleOverrides[postId] = roundRule(draft, rule);
}

export function addPost(draft: Dataset, name: string, currency: Currency, folderId: FolderId | null = null): Post {
  if (folderId !== null) requireFolder(draft, folderId);
  const post: Post = {
    id: newId(),
    name,
    order: nextSiblingOrder(draft, folderId),
    folderId,
    archived: false,
    currency,
    rules: [],
  };
  draft.posts.push(post);
  return post;
}

/** Give a loose purchase an envelope without changing its amount or schedule. */
export function createPostForPurchase(draft: Dataset, purchaseId: PurchaseId): Post {
  const purchase = requirePurchase(draft, purchaseId);
  if (purchase.splits.length > 0) throw new Error("Purchase is already assigned to a post");
  const post = addPost(draft, purchase.description.trim() || "Purchase", purchase.total.currency, purchase.placement?.folderId ?? null);
  purchase.splits = [{ postId: post.id, value: 100, absorbsRemainder: true }];
  purchase.splitMode = "percent";
  return post;
}

export function updatePost(
  draft: Dataset,
  postId: PostId,
  changes: Partial<Omit<Post, "id" | "folderId" | "order">>,
): void {
  const post = requirePost(draft, postId);
  if ("folderId" in changes || "order" in changes) throw new Error("Use placeBudgetItem to move a post");
  const resolved: Partial<Omit<Post, "id" | "folderId" | "order">> = { ...changes };
  Object.assign(post, resolved);
}

/** Archives rather than deletes, because purchases reference posts. */
export function setPostArchived(
  draft: Dataset,
  postId: PostId,
  archived: boolean,
): void {
  requirePost(draft, postId).archived = archived;
}

export function movePost(draft: Dataset, postId: PostId, direction: -1 | 1): void {
  const post = requirePost(draft, postId);
  const ordered = budgetSiblings(draft, post.folderId);
  const index = ordered.findIndex(item => item.kind === "post" && item.id === postId);
  const target = index + direction;
  if (target < 0 || target >= ordered.length) return;
  placeBudgetItem(draft, { kind: "post", id: postId }, post.folderId, target);
}

/**
 * Inserts or replaces the version starting at `from`, keeping `rules` sorted.
 * Replacing rather than appending is what keeps the effective rule
 * unambiguous: two versions sharing a month would both be "in effect", and
 * ruleAt would resolve to whichever the sort happened to leave last.
 */
export function setRuleFrom(
  draft: Dataset,
  postId: PostId,
  from: MonthId,
  rule: Rule,
): void {
  const post = requirePost(draft, postId);
  const version: RuleVersion = { from, rule: roundRule(draft, rule) };
  const existing = post.rules.findIndex((v) => v.from === from);
  if (existing === -1) post.rules.push(version);
  else post.rules[existing] = version;
  post.rules.sort((a, b) => compareMonths(a.from, b.from));
}

/**
 * Deletes the version starting at `from`. Removing the earliest one means the
 * months before the next version now resolve to no rule, and allocate zero.
 */
export function removeRuleFrom(draft: Dataset, postId: PostId, from: MonthId): void {
  const post = requirePost(draft, postId);
  post.rules = post.rules.filter((v) => v.from !== from);
}

export function addPurchase(
  draft: Dataset,
  purchase: Omit<Purchase, "id">,
): Purchase {
  requireSourcePairing(draft, purchase.source, purchase.date);
  const created: Purchase = {
    ...purchase,
    id: newId(),
    total: roundMoneyValue(draft, purchase.total),
    splits: roundSplits(draft, purchase.splits, purchase.splitMode, purchase.total.currency),
    schedule: roundSchedule(draft, purchase.schedule),
  };
  draft.purchases.push(created);
  // Make sure every month the purchase touches exists, so income can be
  // entered against it later.
  const months = created.schedule
    ? created.schedule.slices.map((s) => s.month)
    : [monthOf(created.date)];
  for (const monthId of months) ensureMonth(draft, monthId);
  return created;
}

export function updatePurchase(
  draft: Dataset,
  purchaseId: PurchaseId,
  changes: Partial<Omit<Purchase, "id">>,
): void {
  const purchase = requirePurchase(draft, purchaseId);
  const resolved: Partial<Omit<Purchase, "id">> = { ...changes };
  // The RESOLVED pair: the incoming `source` if this update carries the key,
  // else the one already stored — so touching an unrelated field still
  // re-validates whatever source is already there, and a `source` this
  // update DOES set is checked against the cost it now names. Key presence,
  // not `??`: `source: undefined` CLEARS the association (what
  // `PurchaseDialog` sends for "None"), and `??` would validate the stored
  // tag the write is removing.
  const source = "source" in changes ? changes.source : purchase.source;
  requireSourcePairing(draft, source, changes.date ?? purchase.date);
  if (changes.total) {
    resolved.total = roundMoneyValue(draft, changes.total);
  }
  if (changes.splits) {
    // Either field may be absent from a partial update; the stored purchase
    // supplies whichever one is, so the mode and currency always agree with
    // the values being rounded.
    resolved.splits = roundSplits(
      draft,
      changes.splits,
      changes.splitMode ?? purchase.splitMode,
      (resolved.total ?? purchase.total).currency,
    );
  }
  if (changes.schedule !== undefined) {
    resolved.schedule = roundSchedule(draft, changes.schedule);
  }
  Object.assign(purchase, resolved);
}

export function deletePurchase(draft: Dataset, purchaseId: PurchaseId): void {
  draft.purchases = draft.purchases.filter((p) => p.id !== purchaseId);
}

export function cancelScheduleFrom(
  draft: Dataset,
  purchaseId: PurchaseId,
  monthId: MonthId,
): void {
  const purchase = requirePurchase(draft, purchaseId);
  if (!purchase.schedule) {
    throw new Error(`Purchase ${purchaseId} has no finance plan to cancel`);
  }
  purchase.schedule.cancelledFromMonth = monthId;
}

/**
 * Stores the rate exactly as passed. Exchange rates are not money: they are
 * kept at six decimal places (see fxApi.ts's toFixed(6)), and rounding one to
 * the dataset's decimals would quantize a rate below 0.01 straight to zero.
 */
/** Codes are identity, so they are validated the same way on every path. */
const CURRENCY_CODE = /^[A-Z]{2,8}$/;

function requireCurrencyDef(def: CurrencyDef): CurrencyDef {
  const code = normalizeCurrencyCode(def.code);
  if (!CURRENCY_CODE.test(code)) {
    throw new Error(`Currency code "${def.code}" must be 2-8 letters`);
  }
  return {
    code,
    ...(def.symbol?.trim() ? { symbol: def.symbol.trim() } : {}),
    ...(def.name?.trim() ? { name: def.name.trim() } : {}),
  };
}

export function addCurrency(draft: Dataset, def: CurrencyDef): CurrencyDef {
  const currency = requireCurrencyDef(def);
  if (draft.currencies.some((existing) => existing.code === currency.code)) {
    throw new Error(`Currency ${currency.code} is already defined`);
  }
  draft.currencies.push(currency);
  return currency;
}

export function updateCurrency(
  draft: Dataset,
  code: Currency,
  changes: Partial<Omit<CurrencyDef, "code">>,
): void {
  const existing = draft.currencies.find((currency) => currency.code === code);
  if (!existing) throw new Error(`Unknown currency: ${code}`);
  // The code is identity — it keys the FX table and every stored Money — so it
  // is deliberately not editable. Renaming one means adding the new code,
  // moving what refers to it, and removing the old.
  Object.assign(existing, requireCurrencyDef({ ...existing, ...changes, code }));
}

/**
 * Refuses to remove a currency anything still refers to, naming what refers to
 * it. Its exchange rate goes with it, so no orphan row survives.
 */
export function removeCurrency(draft: Dataset, code: Currency): void {
  const used = currencyUsage(draft, code);
  if (used.length > 0) {
    const isBase = draft.settings.baseCurrency === code;
    throw new Error(
      isBase
        ? `${code} is the base currency; choose another base before removing it`
        : `${code} is in use by ${used.slice(0, 3).join(", ")}${used.length > 3 ? ` and ${used.length - 3} more` : ""}`,
    );
  }
  draft.currencies = draft.currencies.filter((currency) => currency.code !== code);
  draft.fxRates = draft.fxRates.filter((rate) => rate.currency !== code);
}

export function setFxRate(draft: Dataset, rate: FxRate): void {
  const index = draft.fxRates.findIndex((r) => r.currency === rate.currency);
  if (index === -1) draft.fxRates.push(rate);
  else draft.fxRates[index] = rate;
}

/**
 * Removes a currency's rate row entirely. A cleared rate MUST become an
 * absent row, not a stored 0: toBase() throws MissingRateError for an absent
 * row but silently converts to 0 for a stored zero rate, which would corrupt
 * every purchase in that currency without any error surfacing. Callers (the
 * currency settings input) must route a cleared/invalid rate here instead of
 * through setFxRate with a 0.
 */
export function removeFxRate(draft: Dataset, currency: Currency): void {
  draft.fxRates = draft.fxRates.filter((r) => r.currency !== currency);
}

/** The base currency never has a rate row of its own. */
/**
 * The decimal places every amount rounds to, dataset-wide.
 *
 * Amounts already stored are deliberately NOT rewritten. Moving the setting is
 * a rounding rule going forward: 49.95 stays 49.95 and displays as 50, while
 * every total recomputes at the new precision. Rewriting each `Money` would be
 * a destructive whole-dataset write — the kind that owes the owner a backup
 * export first — and it is lossy one way, since 2 -> 0 discards what 0 -> 2
 * cannot bring back.
 *
 * Throws rather than clamping: a digit count outside 0-4 is a caller bug, and
 * silently storing 2 instead is how a wrong number becomes invisible. The
 * Settings input guards before calling, so no event handler sees this throw.
 */
export function setDigits(draft: Dataset, digits: number): void {
  if (!Number.isInteger(digits) || digits < 0 || digits > 4) {
    throw new Error("Decimal places must be a whole number between 0 and 4");
  }
  draft.settings.digits = digits;
}

export function setBaseCurrency(draft: Dataset, currency: Currency): void {
  draft.settings.baseCurrency = currency;
  draft.fxRates = draft.fxRates.filter((r) => r.currency !== currency);
}

function requireRecurringCost(draft: Dataset, id: RecurringCostId): RecurringCost {
  const cost = draft.recurring.find((c) => c.id === id);
  if (!cost) throw new Error(`Unknown recurring cost: ${id}`);
  return cost;
}

/**
 * REVIEW ROUND 1 / I2: `addPurchase`/`updatePurchase` used to accept an
 * arbitrary `source` unchecked — only `confirmOccurrence` built a coherent
 * one. That let the store write a dataset that would fail its own
 * re-import, which is exactly the drift the boundary-agreement requirement
 * exists to close. A later task adds a control that hand-builds a `source`
 * and passes it straight to `addPurchase`, so this has to be closed here, at
 * the store layer, rather than becoming a validation that control's own task
 * has to carry.
 *
 * Mirrors the pairing `json.ts` enforces at the import boundary: a `source`
 * naming a calendar cost REQUIRES an `occurrenceDate` (a charge against a
 * fixed grid settles one slot); a `source` naming a `lastCharge` cost
 * FORBIDS one (an anchored cost has no grid, so a slot id would describe a
 * schedule this app will not compute). `undefined` (no source at all) is
 * always fine and skips this entirely.
 *
 * `date` is the purchase's OWN date — the resolved one, so an update that
 * touches only the date is checked against the source already stored. An
 * anchored cost's next due date is measured from it (`chargeDateNeedsDay`), so
 * a month-only date there is not a display preference but a schedule the
 * anchored walk cannot compute: it would reach `addDays`/`addMonthsToDate`
 * from the fold and take every money route down. Refuse it here, where the
 * owner is still looking at the purchase.
 */
function requireSourcePairing(
  draft: Dataset,
  source: Purchase["source"],
  date: IsoDate,
): void {
  if (source === undefined) return;
  const cost = requireRecurringCost(draft, source.recurringId);
  if (cost.anchoring === "lastCharge") {
    if (source.occurrenceDate !== undefined) {
      throw new Error(
        `"${cost.name}" is anchored to its last charge and has no slots to settle, so a purchase ` +
          `referencing it must not carry an occurrence date`,
      );
    }
  } else if (source.occurrenceDate === undefined) {
    throw new Error(
      `"${cost.name}" is calendar-anchored, so a purchase referencing it must carry the occurrence ` +
        `date (slot) it settles`,
    );
  }

  if (!chargeDateNeedsDay(cost.anchoring)) return;
  if (!isDayGranularDate(date)) {
    throw new Error(
      `"${cost.name}" is anchored to its last charge, so this purchase's own date is what the next ` +
        `charge is measured from and needs a day ("YYYY-MM-DD"), not "${date}"`,
    );
  }
  // Day-SHAPED is not day-VALID: "2026-09-31" would pass the check above and
  // then throw out of the fold, exactly the way an impossible `startDate`
  // would without `requireStartDateGranularity`'s parse below. Same domain
  // parser, same translation into this file's error style.
  try {
    toDayOrdinal(date);
  } catch (error) {
    throw new Error(
      `"${cost.name}" is anchored to its last charge, so this purchase needs a real date, not ` +
        `"${date}" (${(error as Error).message})`,
    );
  }
}

/**
 * REVIEW ROUND 1: pre-existing asymmetry with `json.ts`'s `RECURRENCE_KINDS`
 * check — this file relied on the `Recurrence` union alone, which only binds
 * a caller that goes through the type checker. A hand-edited object or an
 * `as` cast reaches this function directly (this is the store's write path,
 * not the JSON one), so the three known kinds are checked here too now.
 */
const RECURRENCE_KINDS = new Set(["everyNMonths", "everyNDays", "everyNWeeks"]);

/** Mirrors `json.ts`'s `ANCHORINGS` check for the same reason: the `Anchoring`
 * union only binds a typed caller. */
const ANCHORINGS = new Set(["calendar", "lastCharge"]);

/**
 * Guards against a value TypeScript's `Anchoring` union would otherwise
 * guarantee — but only for a caller the type checker actually saw. A
 * hand-edited object or an `as` cast reaches this function directly.
 */
function requireAnchoring(anchoring: Anchoring): Anchoring {
  if (!ANCHORINGS.has(anchoring)) {
    throw new Error(`Anchoring must be "calendar" or "lastCharge", not "${anchoring}"`);
  }
  return anchoring;
}

/**
 * The projection walk in `domain/occurrences.ts` terminates only if every step
 * strictly advances, and `n` is what guarantees that. The JSON importer checks
 * the same thing; this is the other write path, and both have to hold or the
 * fold can hang.
 */
function requireRecurrence(recurrence: Recurrence): Recurrence {
  if (!RECURRENCE_KINDS.has(recurrence.kind)) {
    throw new Error(`A recurrence must be "everyNMonths", "everyNDays", or "everyNWeeks", not "${recurrence.kind}"`);
  }
  if (!Number.isInteger(recurrence.n)) {
    throw new Error(`A recurrence interval must be a whole number, not ${recurrence.n}`);
  }
  if (recurrence.n < 1) {
    throw new Error(`A recurrence interval must be at least 1, not ${recurrence.n}`);
  }
  if (recurrence.kind === "everyNWeeks") {
    if (!Number.isInteger(recurrence.weekday) || recurrence.weekday < 0 || recurrence.weekday > 6) {
      throw new Error(`A weekday must be 0-6 (0 is Sunday), not ${recurrence.weekday}`);
    }
  }
  return recurrence;
}

/**
 * Which pairings need a day is `startDateNeedsDay` in
 * `domain/granularity.ts`, stated once and called from here, from the import
 * boundary and from both controls that offer these choices — the rule used to
 * be written out separately in each of those four places. Enforced HERE, at
 * the boundary, rather than three months later when a fold walks off the end
 * of a date it cannot step from.
 *
 * Shape alone is not enough: "2026-09-31" is day-SHAPED but calendar-
 * impossible (September has 30 days), and would otherwise reach `addDays` (or
 * sit unnoticed under `everyNMonths`) and throw three months later out of the
 * fold instead of here. Route a day-granular value through `toDayOrdinal` and
 * a month-granular one through `monthOf` — both already throw on exactly
 * that — and translate whatever they throw into this file's own error style.
 */
function requireStartDateGranularity(
  recurrence: Recurrence,
  startDate: IsoDate,
  anchoring: Anchoring,
): void {
  const dayGranular = isDayGranularDate(startDate);
  if (startDateNeedsDay(recurrence.kind, anchoring) && !dayGranular) {
    throw new Error(
      `A "${recurrence.kind}" recurrence anchored to ${anchoring} needs a day-granular startDate ("YYYY-MM-DD"), not "${startDate}"`,
    );
  }
  try {
    if (dayGranular) toDayOrdinal(startDate);
    else monthOf(startDate);
  } catch (error) {
    throw new Error(
      `A "${recurrence.kind}" recurrence needs a valid startDate, not "${startDate}" (${(error as Error).message})`,
    );
  }
}

export function addRecurringCost(
  draft: Dataset,
  cost: Omit<RecurringCost, "id" | "order" | "parentPostId">,
): RecurringCost {
  const anchoring = requireAnchoring(cost.anchoring);
  const recurrence = requireRecurrence(cost.recurrence);
  requireStartDateGranularity(recurrence, cost.startDate, anchoring);
  if ("parentPostId" in cost) throw new Error("Use placeRecurringUnderPost to nest a recurring cost");
  if (cost.folderId !== null) requireFolder(draft, cost.folderId);
  requireRecurringSplitDestinations(draft, cost.splits);
  const created: RecurringCost = {
    ...cost,
    id: newId(),
    order: nextSiblingOrder(draft, cost.folderId),
    recurrence,
    anchoring,
    amount: roundMoneyValue(draft, cost.amount),
    splits: roundSplits(draft, cost.splits, cost.splitMode, cost.amount.currency),
  };
  draft.recurring.push(created);
  return created;
}

/** Creates the dedicated envelope and its bill in one store mutation. */
export function addRecurringCostWithPost(
  draft: Dataset,
  cost: Omit<RecurringCost, "id" | "order" | "splits" | "splitMode" | "parentPostId">,
  newPostName: string,
): RecurringCost {
  const name = newPostName.trim();
  if (!name) throw new Error("Post name cannot be blank");
  const recurrence = requireRecurrence(cost.recurrence);
  requireStartDateGranularity(recurrence, cost.startDate, requireAnchoring(cost.anchoring));
  const post = addPost(draft, name, cost.amount.currency, cost.folderId);
  const bill = addRecurringCost(draft, {
    ...cost,
    splitMode: "percent",
    splits: [{ postId: post.id, value: 100, absorbsRemainder: true }],
  });
  placeRecurringUnderPost(draft, bill.id, post.id);
  const monthly = recurrence.kind === "everyNMonths"
    ? bill.amount.amount / recurrence.n
    : recurrence.kind === "everyNWeeks"
      ? bill.amount.amount * 52 / (12 * recurrence.n)
      : bill.amount.amount * 365 / (12 * recurrence.n);
  const factor = 10 ** draft.settings.digits;
  const allocation = roundMoney(Math.ceil(monthly * factor) / factor, draft.settings.digits);
  setRuleFrom(draft, post.id, monthOf(cost.startDate), {
    kind: "fixed", amount: { amount: allocation, currency: cost.amount.currency },
  });
  return bill;
}

/**
 * `archived` and `endedFrom` are excluded on purpose: they are a coherent
 * pair (`archived` mirrors "`endedFrom` is set") maintained ONLY by
 * `setRecurringCostEndedFrom`, and a caller able to pass either through here
 * could desynchronise them — a bill that reads as live but is dimmed, or
 * ended but not. Route a change to either field through
 * `setRecurringCostEndedFrom` (or its `endRecurringCost`/`restoreRecurringCost`
 * convenience wrappers) instead.
 */
export function updateRecurringCost(
  draft: Dataset,
  id: RecurringCostId,
  changes: Partial<Omit<RecurringCost, "id" | "archived" | "endedFrom" | "folderId" | "order" | "parentPostId">>,
): void {
  const cost = requireRecurringCost(draft, id);
  if ("folderId" in changes || "order" in changes || "parentPostId" in changes) throw new Error("Use placement actions to move a recurring cost");
  const resolved: Partial<Omit<RecurringCost, "id" | "archived" | "endedFrom" | "folderId" | "order">> = { ...changes };

  if (changes.anchoring) {
    requireAnchoring(changes.anchoring);
  }

  // REVIEW ROUND 1 / M2: checked BEFORE requireStartDateGranularity below.
  // A charged cost's anchoring change is refused outright, no matter what
  // else is wrong with the request — checking the date pairing first meant
  // flipping a charged month-only calendar cost to lastCharge reported the
  // date problem, the owner "fixed" the date and resubmitted, and only then
  // learned the change was never allowed at all.
  if (changes.anchoring && changes.anchoring !== cost.anchoring) {
    // Switching calendar -> lastCharge could strip the now-forbidden slot ids
    // mechanically, but the reverse cannot: there is no honest way to invent
    // which slot each existing charge settled, and guessing would leave real
    // money claiming slots that are not on the new grid. Ending the cost and
    // creating a new one keeps the history and states the change plainly.
    const charged = draft.purchases.some((p) => p.source?.recurringId === cost.id);
    if (charged) {
      throw new Error(
        `"${cost.name}" already has charges recorded, so its anchoring cannot change. ` +
          `End this cost and create a new one with the anchoring you want — its history stays intact.`,
      );
    }
  }

  if (changes.recurrence) {
    resolved.recurrence = requireRecurrence(changes.recurrence);
  }
  // Runs on every update, not just one that touches recurrence or startDate:
  // either field alone can break the pairing (a kind flip against an
  // untouched month-only startDate is exactly how this bug was reached), and
  // an anchoring flip against an untouched month-only startDate is the same
  // shape of bug one level up.
  requireStartDateGranularity(
    resolved.recurrence ?? cost.recurrence,
    resolved.startDate ?? cost.startDate,
    changes.anchoring ?? cost.anchoring,
  );

  if (changes.amount) {
    resolved.amount = roundMoneyValue(draft, changes.amount);
  }
  if (changes.splits) {
    requireRecurringSplitDestinations(draft, changes.splits);
    // Either field may be absent from a partial update; the stored cost
    // supplies whichever one is, so the mode and currency always agree with
    // the values being rounded.
    resolved.splits = roundSplits(
      draft,
      changes.splits,
      changes.splitMode ?? cost.splitMode,
      (resolved.amount ?? cost.amount).currency,
    );
  }

  Object.assign(cost, resolved);
}

export function moveRecurringCost(
  draft: Dataset,
  id: RecurringCostId,
  direction: -1 | 1,
): void {
  const cost = requireRecurringCost(draft, id);
  const ordered = budgetSiblings(draft, cost.folderId);
  const index = ordered.findIndex(item => item.kind === "recurring" && item.id === id);
  const target = index + direction;
  if (target < 0 || target >= ordered.length) return;
  placeBudgetItem(draft, { kind: "recurring", id }, cost.folderId, target);
}

/**
 * Sets or clears `endedFrom` directly — what the Settings "Ends" column edits
 * — keeping `archived` coherent with it. The rule: `archived` mirrors
 * "`endedFrom` is set", same as `endRecurringCost`/`restoreRecurringCost`
 * below, which now both delegate here. That is what stops the two fields
 * diverging into a bill that reads as live but is dimmed, or ended but not.
 *
 * Both fields, because they mean different things: `endedFrom` stops the
 * PROJECTION, and `archived` only flags the row — same as a post, it stays
 * listed (sorted in, rendered at reduced opacity) rather than being filtered
 * out. Archiving alone would leave the bill projecting forever; setting
 * `endedFrom` alone would leave a dead bill undimmed in the list. Neither
 * touches a past occurrence, so no historical figure moves.
 *
 * `endedFrom` itself is unchecked here — no granularity rule applies to it
 * (§10; it is only ever compared lexicographically, never reaches
 * `addDays`) — so the caller validates it is a real `IsoDate` before this is
 * called, the same way the "Starts" field already does for `startDate`.
 */
export function setRecurringCostEndedFrom(
  draft: Dataset,
  id: RecurringCostId,
  endedFrom: IsoDate | undefined,
): void {
  const cost = requireRecurringCost(draft, id);
  if (endedFrom === undefined) {
    delete cost.endedFrom;
    cost.archived = false;
  } else {
    cost.endedFrom = endedFrom;
    cost.archived = true;
  }
}

/** The one-click convenience: stop a bill from `from` onward and dim it. */
export function endRecurringCost(draft: Dataset, id: RecurringCostId, from: IsoDate): void {
  setRecurringCostEndedFrom(draft, id, from);
}

/** The one-click convenience: clear the cancellation and un-dim the row. */
export function restoreRecurringCost(draft: Dataset, id: RecurringCostId): void {
  setRecurringCostEndedFrom(draft, id, undefined);
}

/**
 * Turns one projected occurrence into a real purchase.
 *
 * `occurrenceDate` is the slot being claimed and goes into `source`;
 * `overrides.date` is when the money actually moved and goes on the purchase.
 * They differ whenever a bill is paid off schedule, and under `lastCharge`
 * anchoring it is the latter that rebases the series — which is the whole of
 * the phone-bill behaviour.
 *
 * A `lastCharge` cost has no grid, so its charge names no slot: `source`
 * carries `recurringId` alone, and `occurrenceDate` — the caller's projected
 * cursor, not a real slot — is dropped rather than stored. Recording it would
 * describe a schedule the app will not compute (`confirmationsFor` in
 * `occurrences.ts` is calendar-only and throws on exactly this shape).
 *
 * Deleting the purchase later un-confirms the slot, with nothing to reconcile.
 */
export function confirmOccurrence(
  draft: Dataset,
  recurringId: RecurringCostId,
  occurrenceDate: IsoDate,
  overrides: { date?: IsoDate; amount?: Money } = {},
): Purchase {
  const cost = requireRecurringCost(draft, recurringId);
  return addPurchase(draft, {
    date: overrides.date ?? occurrenceDate,
    description: cost.name,
    total: overrides.amount ?? cost.amount,
    splitMode: cost.splitMode,
    // Copied, not shared: editing the purchase's splits must not rewrite the
    // cost's, and `mutate` clones the draft rather than deep-freezing it.
    splits: cost.splits.map((split) => ({ ...split })),
    schedule: null,
    source:
      cost.anchoring === "lastCharge"
        ? { recurringId }
        : { recurringId, occurrenceDate },
  });
}
